import { useState } from 'react';

import { motion } from 'framer-motion';
import styled from 'styled-components';

import { AddProfileImage } from '@components/add-profile-image';
import { Breadcrumb } from '@components/breadcrumb';
import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { Header } from '@components/header';
import { Spinner } from '@components/loading-spinner';
import { PageContent } from '@components/page-containers/page-container';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { StandaloneThemeToggle } from '@components/theme-provider';
import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useEditMyDetails, useMyDetails } from 'api/users/hooks';

import { UserStatus } from '../organization/user-list';

type MyDetailsFormData = {
  name: string;
  email: string;
  phone: string;
};

const Form = createForm<MyDetailsFormData>();

export const MyDetails = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();

  const [picture, setPicture] = useState<File | null>();
  const [clearPicture, setClearPicture] = useState(false);
  const [emailError, setEmailError] = useState([] as string[]);

  const formStore = useCreateFormStore();
  const name = formStore.useFieldValue('name') as string;
  const isDirty = formStore.useIsDirty() || clearPicture || picture;

  const detailsQuery = useMyDetails();
  const editMutation = useEditMyDetails(setEmailError, {
    onSuccess: () => {
      showSuccess(t('userEdited'));
    },
  });

  if (detailsQuery.isLoading) return <Spinner />;
  if (!detailsQuery.data || detailsQuery.isError) return null;

  const user = detailsQuery.data;

  const clearProfileImage = () => {
    if (picture) {
      setPicture(null);
    }
    if (clearPicture) {
      setClearPicture(false);
    }
  };

  const handleSave = async (data: MyDetailsFormData) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('phone', data.phone ?? '');

    if (picture) {
      formData.append('picture', picture);
    } else if (clearPicture) {
      formData.append('picture', '');
    }
    await editMutation.mutateAsync(formData);
    clearProfileImage();
  };

  return (
    <>
      <Header breadcrumbs={<Breadcrumb items={[{ label: t('profile') }]} />} />
      <PageContent>
        <DetailContainer>
          <Stack space={8}>
            <Form store={formStore} onSubmit={handleSave}>
              <Stack space={4} orientation="horizontal" align="center">
                <AddProfileImage
                  name={name}
                  initalPicture={user?.picture}
                  picture={picture}
                  setPicture={setPicture}
                  clearPicture={clearPicture}
                  setClearPicture={setClearPicture}
                />
                <div>
                  <Text font="bold18" text={user.name} />
                  <Text color="textSecondary" text={user.email} />
                </div>
              </Stack>
              <Stack space={2} orientation="horizontal" align="center">
                <UserStatus status={user.status} />
                <Text text={'•'} color="textHelper" />
                <Text
                  font="regular14"
                  color="textHelper"
                  text={`${abbreviateDate(user.dateCreated, 'en')}`}
                />
              </Stack>
              <Card space={4} variant="transparent">
                <Stack space={4}>
                  <Form.TextField
                    id="name"
                    label={t('core.fullName')}
                    validators={['required']}
                    defaultValue={user.name}
                  />
                  <Form.TextField
                    id="email"
                    label={t('core.email')}
                    validators={['required', 'email']}
                    defaultValue={user.email}
                    onChange={() => setEmailError([])}
                  />
                  {emailError && <FormError text={(emailError ?? []).join(' ')} />}
                  <Form.PhoneField
                    id="phone"
                    label={t('core.phone')}
                    validators={[]}
                    defaultValue={user.phone}
                  />
                  {isDirty && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <Form.Actions>
                        <Form.Reset
                          text={t('core.cancel')}
                          variant="secondary"
                          onClick={() => {
                            clearProfileImage();
                          }}
                        />
                        <Form.Submit text={t('core.save')} variant="primary-blue" />
                      </Form.Actions>
                    </motion.div>
                  )}
                </Stack>
              </Card>
              <Card space={4} variant="transparent">
                <StandaloneThemeToggle />
              </Card>
            </Form>
          </Stack>
        </DetailContainer>
      </PageContent>
    </>
  );
};

const DetailContainer = styled.div`
  max-width: 35.25rem;
  margin: 0 auto;
`;

export const locale = {
  en: {
    profile: 'Profile',
    userEdited: 'User updated',
    oldBuilderViewportLabel: 'Old style builder zoom/pan',
    oldBuilderViewportInfo:
      'We changed the way the builder zooms and pans to the more common way.\nIf you prefer the old style, you can switch it back here.\n\nWhen on:\nPan: drag mouse\nZoom: scroll\n\nWhen off:\nPan: Space + drag mouse, scroll, middle or right mouse button\nZoom: pinch or cmd/windows + scroll',
  },
};
